import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, ArrowLeft, Inbox, Rocket, Upload, BarChart3, Bell, Shirt, Chrome, ShoppingBag, Landmark } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { LogoMark } from "@/components/Logo";
import { CATEGORIES, CONDITIONS, STATUSES, imgSrc } from "@/lib/constants";

const emptyForm = {
  title: "",
  brand: "",
  category: "Hoodies",
  condition: "neu",
  size: "",
  price: "",
  image_url: "",
  description_de: "",
  description_en: "",
  status: "available",
};

const emptyLookForm = {
  title_de: "",
  title_en: "",
  description_de: "",
  description_en: "",
  image_url: "",
  product_codes: [],
};

const emptySettings = {
  drop_enabled: false,
  next_drop_at: "",
  drop_title_de: "",
  drop_title_en: "",
  bank_holder: "",
  bank_iban: "",
  bank_name: "",
  bank_bic: "",
};

const inputCls =
  "w-full border border-edge bg-obsidian px-3.5 py-2.5 font-grotesk text-sm text-ink placeholder:text-mutedink/60 focus:border-volt focus:outline-none";

const statusChip = {
  available: "bg-volt/10 text-volt",
  reserved: "bg-reservedamber/15 text-reservedamber",
  sold: "bg-signal/10 text-signal",
};

const orderChip = {
  pending: "bg-reservedamber/15 text-reservedamber",
  paid: "bg-okgreen/15 text-okgreen",
  cancelled: "bg-mutedink/10 text-mutedink",
};

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-mutedink">{label}</span>
    {children}
  </label>
);

export default function AdminPage() {
  const { t, lang, setLang } = useLang();
  const [token, setToken] = useState(() => localStorage.getItem("vintora_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [looks, setLooks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [lookEditing, setLookEditing] = useState(null);
  const [lookForm, setLookForm] = useState(emptyLookForm);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState(emptySettings);

  const location = useLocation();

  const logout = () => {
    localStorage.removeItem("vintora_token");
    api.post("/auth/logout").catch(() => {});
    setToken(null);
  };

  useEffect(() => {
    if (token) return;
    if (location.state?.user) {
      setToken("google");
      return;
    }
    api.get("/auth/me").then(() => setToken("google")).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleAuthError = (err) => {
    if (err.response?.status === 401) {
      logout();
      return true;
    }
    return false;
  };

  const loadProducts = () =>
    api.get("/products").then((res) => setProducts(res.data)).catch((err) => {
      if (!handleAuthError(err)) toast.error(t.admin.loadError);
    });

  const loadMessages = () =>
    api.get("/admin/messages").then((res) => setMessages(res.data)).catch((err) => {
      if (!handleAuthError(err)) toast.error(t.admin.loadError);
    });

  const loadStats = () =>
    api.get("/admin/stats").then((res) => setStats(res.data)).catch((err) => {
      if (!handleAuthError(err)) toast.error(t.admin.loadError);
    });

  const loadSubscribers = () =>
    api.get("/admin/subscribers").then((res) => setSubscribers(res.data)).catch((err) => {
      if (!handleAuthError(err)) toast.error(t.admin.loadError);
    });

  const loadLooks = () =>
    api.get("/looks").then((res) => setLooks(res.data)).catch((err) => {
      if (!handleAuthError(err)) toast.error(t.admin.loadError);
    });

  const loadOrders = () =>
    api.get("/admin/orders").then((res) => setOrders(res.data)).catch((err) => {
      if (!handleAuthError(err)) toast.error(t.admin.loadError);
    });

  const loadSettings = () =>
    api
      .get("/admin/settings")
      .then((res) =>
        setSettings({
          drop_enabled: !!res.data.drop_enabled,
          next_drop_at: res.data.next_drop_at ? res.data.next_drop_at.slice(0, 16) : "",
          drop_title_de: res.data.drop_title_de || "",
          drop_title_en: res.data.drop_title_en || "",
          bank_holder: res.data.bank_holder || "",
          bank_iban: res.data.bank_iban || "",
          bank_name: res.data.bank_name || "",
          bank_bic: res.data.bank_bic || "",
        })
      )
      .catch(() => {});

  useEffect(() => {
    if (token) {
      loadProducts();
      loadMessages();
      loadStats();
      loadSubscribers();
      loadLooks();
      loadOrders();
      loadSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      localStorage.setItem("vintora_token", res.data.token);
      setToken(res.data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.admin.loginError);
    } finally {
      setLoggingIn(false);
    }
  };

  const startEdit = (p) => {
    setEditing(p || "new");
    setForm(
      p
        ? { ...p, price: String(p.price), status: p.status || (p.is_available ? "available" : "sold") }
        : emptyForm
    );
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    delete payload.id;
    delete payload.code;
    delete payload.created_at;
    delete payload.is_available;
    delete payload.stats;
    try {
      if (editing === "new") {
        await api.post("/admin/products", payload);
      } else {
        await api.put(`/admin/products/${editing.id}`, payload);
      }
      toast.success(t.admin.saved);
      setEditing(null);
      loadProducts();
      loadStats();
    } catch (err) {
      if (!handleAuthError(err)) toast.error(t.admin.loadError);
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(t.admin.confirmDelete)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success(t.admin.deleted);
      loadProducts();
      loadStats();
    } catch (err) {
      if (!handleAuthError(err)) toast.error(t.admin.loadError);
    }
  };

  const uploadFile = async (e, apply) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd);
      apply(res.data.url);
      toast.success(t.admin.saved);
    } catch (err) {
      if (!handleAuthError(err)) toast.error(t.admin.uploadError);
    } finally {
      setUploading(false);
    }
  };

  const saveSettings = async (e, successMsg) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", {
        ...settings,
        next_drop_at: settings.next_drop_at ? new Date(settings.next_drop_at).toISOString() : null,
      });
      toast.success(successMsg);
    } catch (err) {
      if (!handleAuthError(err)) toast.error(t.admin.loadError);
    }
  };

  const orderAction = async (order, status) => {
    try {
      await api.put(`/admin/orders/${order.id}/status`, { status });
      toast.success(t.admin.saved);
      loadOrders();
      loadProducts();
    } catch (err) {
      if (!handleAuthError(err)) toast.error(t.admin.loadError);
    }
  };

  const startLookEdit = (l) => {
    setLookEditing(l || "new");
    setLookForm(l ? { title_de: l.title_de, title_en: l.title_en || "", description_de: l.description_de || "", description_en: l.description_en || "", image_url: l.image_url, product_codes: l.product_codes || [] } : emptyLookForm);
  };

  const toggleLookProduct = (code) => {
    setLookForm((f) => ({
      ...f,
      product_codes: f.product_codes.includes(code) ? f.product_codes.filter((c) => c !== code) : [...f.product_codes, code],
    }));
  };

  const saveLook = async (e) => {
    e.preventDefault();
    try {
      if (lookEditing === "new") {
        await api.post("/admin/looks", lookForm);
      } else {
        await api.put(`/admin/looks/${lookEditing.id}`, lookForm);
      }
      toast.success(t.admin.saved);
      setLookEditing(null);
      loadLooks();
    } catch (err) {
      if (!handleAuthError(err)) toast.error(t.admin.loadError);
    }
  };

  const deleteLook = async (l) => {
    if (!window.confirm(t.admin.confirmDeleteLook)) return;
    try {
      await api.delete(`/admin/looks/${l.id}`);
      toast.success(t.admin.deleted);
      loadLooks();
    } catch (err) {
      if (!handleAuthError(err)) toast.error(t.admin.loadError);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-obsidian px-5" data-testid="admin-login-page">
        <div className="grain-overlay" />
        <form onSubmit={submitLogin} className="w-full max-w-sm border border-edge bg-surface p-8 shadow-[0_2px_16px_rgba(18,20,23,0.06)]" data-testid="admin-login-form">
          <div className="mb-8 flex flex-col items-center gap-3">
            <LogoMark size={44} />
            <h1 className="font-display text-xl font-bold tracking-tight text-ink" data-testid="admin-login-title">{t.admin.loginTitle}</h1>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mutedink">{t.admin.loginSub}</p>
          </div>
          <Field label={t.admin.email} testid="admin-email-field">
            <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="admin@hellovintora.com" />
          </Field>
          <div className="mt-4">
            <Field label={t.admin.password} testid="admin-password-field">
              <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" />
            </Field>
          </div>
          <button data-testid="admin-login-submit-button" type="submit" disabled={loggingIn} className="mt-6 w-full bg-volt py-3.5 font-grotesk text-sm font-bold uppercase tracking-widest text-obsidian transition-colors hover:bg-volt-hover disabled:opacity-50">
            {loggingIn ? t.admin.loggingIn : t.admin.login}
          </button>
          <div className="mt-4 flex items-center gap-3">
            <span className="h-px flex-1 bg-edge" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-mutedink/60">{t.admin.orDivider}</span>
            <span className="h-px flex-1 bg-edge" />
          </div>
          <button
            data-testid="admin-google-login-button"
            type="button"
            onClick={loginWithGoogle}
            className="mt-4 flex w-full items-center justify-center gap-2 border border-edge py-3.5 font-grotesk text-sm font-bold uppercase tracking-widest text-ink transition-colors hover:border-edge-bright hover:bg-surface-hover"
          >
            <Chrome size={15} /> {t.admin.googleLogin}
          </button>
          <Link to="/" data-testid="admin-back-link" className="mt-5 flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-widest text-mutedink transition-colors hover:text-ink">
            <ArrowLeft size={12} /> {t.admin.backToSite}
          </Link>
        </form>
      </div>
    );
  }

  const tabCls = (name) =>
    `border px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest transition-colors ${
      tab === name ? "border-volt bg-volt text-obsidian" : "border-edge bg-surface text-mutedink hover:text-ink"
    }`;

  const productStatus = (p) => p.status || (p.is_available ? "available" : "sold");

  const fmtDate = (iso) => new Date(iso).toLocaleString(lang === "de" ? "de-DE" : "en-GB");

  const imagePicker = (value, apply, inputTestid, uploadTestid, previewTestid) => (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <label
          data-testid={uploadTestid}
          className={`flex cursor-pointer items-center gap-2 border border-volt px-4 py-2.5 font-grotesk text-xs font-bold uppercase tracking-widest text-volt transition-colors hover:bg-volt hover:text-obsidian ${uploading ? "pointer-events-none opacity-50" : ""}`}
        >
          <Upload size={14} />
          {uploading ? t.admin.uploading : t.admin.upload}
          <input data-testid={`${uploadTestid}-input`} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => uploadFile(e, apply)} />
        </label>
        <span className="font-mono text-[10px] uppercase tracking-widest text-mutedink">{t.admin.uploadOr}</span>
        <input data-testid={inputTestid} required value={value} onChange={(e) => apply(e.target.value)} className={`${inputCls} min-w-0 flex-1`} placeholder="https://…" />
      </div>
      {value && (
        <img src={imgSrc(value)} alt="preview" data-testid={previewTestid} className="mt-3 h-32 w-32 border border-edge object-cover" onError={(e) => (e.target.style.display = "none")} onLoad={(e) => (e.target.style.display = "block")} />
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-dashboard">
      <div className="grain-overlay" />
      <header className="sticky top-0 z-40 border-b border-edge bg-[#0B1526]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-5 py-4 md:px-8">
          <div className="flex items-center gap-3">
            <LogoMark size={32} />
            <span className="font-display text-lg font-bold tracking-tight text-ink">
              hello<span className="italic text-volt">vintora</span>
              <span className="ml-3 font-mono text-[10px] font-normal uppercase tracking-[0.25em] text-mutedink">{t.admin.dashboard}</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center border border-edge bg-surface/60" data-testid="admin-language-switcher">
              {["de", "en"].map((l) => (
                <button key={l} data-testid={`admin-lang-${l}-button`} onClick={() => setLang(l)} className={`px-2.5 py-1.5 font-mono text-xs uppercase tracking-widest transition-colors ${lang === l ? "bg-volt text-obsidian" : "text-mutedink hover:text-ink"}`}>
                  {l}
                </button>
              ))}
            </div>
            <Link to="/" data-testid="admin-back-to-site-link" className="flex items-center gap-2 border border-edge bg-surface px-3.5 py-2 font-mono text-[11px] uppercase tracking-widest text-mutedink transition-colors hover:text-ink">
              <ArrowLeft size={12} /> <span className="hidden sm:inline">{t.admin.backToSite}</span>
            </Link>
            <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 border border-edge bg-surface px-3.5 py-2 font-mono text-[11px] uppercase tracking-widest text-mutedink transition-colors hover:border-signal hover:text-signal">
              <LogOut size={12} /> <span className="hidden sm:inline">{t.admin.logout}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 md:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
            <button data-testid="admin-tab-products" onClick={() => setTab("products")} className={tabCls("products")}>
              {t.admin.products} ({products.length})
            </button>
            <button data-testid="admin-tab-looks" onClick={() => setTab("looks")} className={tabCls("looks")}>
              <span className="inline-flex items-center gap-1.5"><Shirt size={12} /> {t.admin.looksTab} ({looks.length})</span>
            </button>
            <button data-testid="admin-tab-orders" onClick={() => { setTab("orders"); loadOrders(); }} className={tabCls("orders")}>
              <span className="inline-flex items-center gap-1.5"><ShoppingBag size={12} /> {t.admin.ordersTab} ({orders.length})</span>
            </button>
            <button data-testid="admin-tab-messages" onClick={() => setTab("messages")} className={tabCls("messages")}>
              {t.admin.messages} ({messages.length})
            </button>
            <button data-testid="admin-tab-stats" onClick={() => { setTab("stats"); loadStats(); }} className={tabCls("stats")}>
              <span className="inline-flex items-center gap-1.5"><BarChart3 size={12} /> {t.admin.statsTab}</span>
            </button>
            <button data-testid="admin-tab-drop" onClick={() => { setTab("drop"); loadSubscribers(); }} className={tabCls("drop")}>
              <span className="inline-flex items-center gap-1.5"><Rocket size={12} /> {t.admin.dropTab}</span>
            </button>
            <button data-testid="admin-tab-bank" onClick={() => setTab("bank")} className={tabCls("bank")}>
              <span className="inline-flex items-center gap-1.5"><Landmark size={12} /> {t.admin.bankTab}</span>
            </button>
          </div>
          {tab === "products" && (
            <button data-testid="admin-add-product-button" onClick={() => startEdit(null)} className="flex items-center gap-2 bg-volt px-4 py-2.5 font-grotesk text-xs font-bold uppercase tracking-widest text-obsidian transition-colors hover:bg-volt-hover">
              <Plus size={14} /> {t.admin.addProduct}
            </button>
          )}
          {tab === "looks" && (
            <button data-testid="admin-add-look-button" onClick={() => startLookEdit(null)} className="flex items-center gap-2 bg-volt px-4 py-2.5 font-grotesk text-xs font-bold uppercase tracking-widest text-obsidian transition-colors hover:bg-volt-hover">
              <Plus size={14} /> {t.admin.addLook}
            </button>
          )}
        </div>

        {tab === "products" && (
          <>
            {editing && (
              <form onSubmit={saveProduct} className="mt-8 border border-edge-bright bg-surface p-6 shadow-[0_2px_16px_rgba(18,20,23,0.05)] md:p-8" data-testid="admin-product-form">
                <h2 className="mb-6 font-display text-lg font-bold tracking-tight text-ink">
                  {editing === "new" ? t.admin.addProduct : t.admin.editProduct}
                </h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <Field label={t.admin.fields.title} testid="form-field-title">
                    <input data-testid="form-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label={t.admin.fields.brand} testid="form-field-brand">
                    <input data-testid="form-brand-input" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label={t.admin.fields.category} testid="form-field-category">
                    <select data-testid="form-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label={t.admin.fields.condition} testid="form-field-condition">
                    <select data-testid="form-condition-select" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className={inputCls}>
                      {CONDITIONS.map((c) => (
                        <option key={c} value={c}>{t.conditions[c]}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label={t.admin.fields.size} testid="form-field-size">
                    <input data-testid="form-size-input" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} className={inputCls} placeholder="L / EU 43 / OS" />
                  </Field>
                  <Field label={t.admin.fields.price} testid="form-field-price">
                    <input data-testid="form-price-input" required type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label={t.admin.fields.status} testid="form-field-status">
                    <select data-testid="form-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{t.admin.statuses[s]}</option>
                      ))}
                    </select>
                  </Field>
                </div>
                <div className="mt-4">
                  <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-mutedink">{t.admin.fields.image}</span>
                  {imagePicker(form.image_url, (v) => setForm((f) => ({ ...f, image_url: v })), "form-image-input", "form-upload-button", "form-image-preview")}
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Field label={t.admin.fields.descDe} testid="form-field-desc-de">
                    <textarea data-testid="form-desc-de-input" rows={3} value={form.description_de} onChange={(e) => setForm({ ...form, description_de: e.target.value })} className={`${inputCls} resize-none`} />
                  </Field>
                  <Field label={t.admin.fields.descEn} testid="form-field-desc-en">
                    <textarea data-testid="form-desc-en-input" rows={3} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className={`${inputCls} resize-none`} />
                  </Field>
                </div>
                <div className="mt-7 flex gap-3">
                  <button data-testid="form-save-button" type="submit" className="bg-volt px-6 py-3 font-grotesk text-xs font-bold uppercase tracking-widest text-obsidian transition-colors hover:bg-volt-hover">
                    {t.admin.save}
                  </button>
                  <button data-testid="form-cancel-button" type="button" onClick={() => setEditing(null)} className="border border-edge px-6 py-3 font-grotesk text-xs uppercase tracking-widest text-mutedink transition-colors hover:text-ink">
                    {t.admin.cancel}
                  </button>
                </div>
              </form>
            )}
            <div className="mt-8 grid grid-cols-1 gap-4" data-testid="admin-product-list">
              {products.map((p) => (
                <div key={p.id} data-testid={`admin-product-row-${p.code}`} className="flex flex-wrap items-center gap-4 border border-edge bg-surface p-4 transition-colors hover:border-edge-bright">
                  <img src={imgSrc(p.image_url)} alt={p.title} className={`h-16 w-16 border border-edge object-cover ${productStatus(p) === "sold" ? "opacity-40 grayscale" : ""}`} />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-mutedink">[{p.code}] · {p.category} · {t.conditions[p.condition]}</div>
                    <div className="truncate font-grotesk font-semibold text-ink">{p.title}</div>
                    <div className="font-mono text-xs text-mutedink">{p.size} · €{p.price}</div>
                  </div>
                  <span className={`px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${statusChip[productStatus(p)]}`} data-testid={`status-${p.code}`}>
                    {t.admin.statuses[productStatus(p)]}
                  </span>
                  <div className="flex gap-2">
                    <button data-testid={`admin-edit-${p.code}`} onClick={() => startEdit(p)} className="flex h-9 w-9 items-center justify-center border border-edge text-mutedink transition-colors hover:border-volt hover:text-volt" aria-label={t.admin.edit}>
                      <Pencil size={14} />
                    </button>
                    <button data-testid={`admin-delete-${p.code}`} onClick={() => deleteProduct(p)} className="flex h-9 w-9 items-center justify-center border border-edge text-mutedink transition-colors hover:border-signal hover:text-signal" aria-label={t.admin.delete}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "looks" && (
          <>
            {lookEditing && (
              <form onSubmit={saveLook} className="mt-8 max-w-3xl border border-edge-bright bg-surface p-6 shadow-[0_2px_16px_rgba(18,20,23,0.05)] md:p-8" data-testid="admin-look-form">
                <h2 className="mb-6 font-display text-lg font-bold tracking-tight text-ink">
                  {lookEditing === "new" ? t.admin.addLook : t.admin.editLook}
                </h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label={t.admin.lookTitleDe} testid="look-field-title-de">
                    <input data-testid="look-title-de-input" required value={lookForm.title_de} onChange={(e) => setLookForm({ ...lookForm, title_de: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label={t.admin.lookTitleEn} testid="look-field-title-en">
                    <input data-testid="look-title-en-input" value={lookForm.title_en} onChange={(e) => setLookForm({ ...lookForm, title_en: e.target.value })} className={inputCls} />
                  </Field>
                  <Field label={t.admin.lookDescDe} testid="look-field-desc-de">
                    <textarea data-testid="look-desc-de-input" rows={2} value={lookForm.description_de} onChange={(e) => setLookForm({ ...lookForm, description_de: e.target.value })} className={`${inputCls} resize-none`} />
                  </Field>
                  <Field label={t.admin.lookDescEn} testid="look-field-desc-en">
                    <textarea data-testid="look-desc-en-input" rows={2} value={lookForm.description_en} onChange={(e) => setLookForm({ ...lookForm, description_en: e.target.value })} className={`${inputCls} resize-none`} />
                  </Field>
                </div>
                <div className="mt-4">
                  <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-mutedink">{t.admin.lookImage}</span>
                  {imagePicker(lookForm.image_url, (v) => setLookForm((f) => ({ ...f, image_url: v })), "look-image-input", "look-upload-button", "look-image-preview")}
                </div>
                <div className="mt-5">
                  <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-mutedink">{t.admin.lookProducts}</span>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="look-products-picker">
                    {products.map((p) => (
                      <label
                        key={p.code}
                        data-testid={`look-product-${p.code}`}
                        className={`flex cursor-pointer items-center gap-3 border px-3.5 py-2.5 transition-colors ${lookForm.product_codes.includes(p.code) ? "border-volt bg-volt/5" : "border-edge hover:border-edge-bright"}`}
                      >
                        <input type="checkbox" checked={lookForm.product_codes.includes(p.code)} onChange={() => toggleLookProduct(p.code)} className="h-4 w-4 accent-[#1B2A4A]" />
                        <span className="min-w-0 font-grotesk text-sm">
                          <span className="mr-2 font-mono text-[10px] text-mutedink">[{p.code}]</span>
                          <span className="text-ink">{p.title}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="mt-7 flex gap-3">
                  <button data-testid="look-save-button" type="submit" className="bg-volt px-6 py-3 font-grotesk text-xs font-bold uppercase tracking-widest text-obsidian transition-colors hover:bg-volt-hover">
                    {t.admin.save}
                  </button>
                  <button data-testid="look-cancel-button" type="button" onClick={() => setLookEditing(null)} className="border border-edge px-6 py-3 font-grotesk text-xs uppercase tracking-widest text-mutedink transition-colors hover:text-ink">
                    {t.admin.cancel}
                  </button>
                </div>
              </form>
            )}
            <div className="mt-8 grid grid-cols-1 gap-4" data-testid="admin-looks-list">
              {looks.length === 0 && !lookEditing ? (
                <div className="border border-dashed border-edge-bright py-16 text-center" data-testid="admin-looks-empty">
                  <span className="font-grotesk text-sm text-mutedink">{t.admin.noLooks}</span>
                </div>
              ) : (
                looks.map((l) => (
                  <div key={l.id} data-testid={`admin-look-row-${l.id}`} className="flex flex-wrap items-center gap-4 border border-edge bg-surface p-4 transition-colors hover:border-edge-bright">
                    <img src={imgSrc(l.image_url)} alt={l.title_de} className="h-16 w-16 border border-edge object-cover" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-grotesk font-semibold text-ink">{l.title_de}</div>
                      <div className="font-mono text-xs text-mutedink">{(l.product_codes || []).join(" · ") || "—"}</div>
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`admin-look-edit-${l.id}`} onClick={() => startLookEdit(l)} className="flex h-9 w-9 items-center justify-center border border-edge text-mutedink transition-colors hover:border-volt hover:text-volt" aria-label={t.admin.edit}>
                        <Pencil size={14} />
                      </button>
                      <button data-testid={`admin-look-delete-${l.id}`} onClick={() => deleteLook(l)} className="flex h-9 w-9 items-center justify-center border border-edge text-mutedink transition-colors hover:border-signal hover:text-signal" aria-label={t.admin.delete}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {tab === "orders" && (
          <div className="mt-8 grid grid-cols-1 gap-4" data-testid="admin-orders-list">
            {orders.length === 0 ? (
              <div className="flex flex-col items-center gap-3 border border-dashed border-edge-bright py-16 text-mutedink" data-testid="admin-orders-empty">
                <ShoppingBag size={28} />
                <span className="font-grotesk text-sm">{t.admin.orders.empty}</span>
              </div>
            ) : (
              orders.map((o) => (
                <div key={o.id} data-testid={`admin-order-${o.id}`} className="border border-edge bg-surface p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-grotesk font-semibold text-ink">{o.product_title} <span className="font-mono text-xs font-normal text-mutedink">[{o.product_code}] · €{o.price}</span></div>
                      <div className="mt-1 font-mono text-xs text-volt" data-testid={`order-ref-${o.id}`}>{o.reference}</div>
                    </div>
                    <span className={`px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${orderChip[o.status] || orderChip.pending}`} data-testid={`order-status-${o.id}`}>
                      {t.admin.orders[o.status] || o.status}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-1 gap-3 border-t border-edge/70 pt-3 text-sm sm:grid-cols-3">
                    <div>
                      <div className="font-mono text-[9px] uppercase tracking-widest text-mutedink">{t.admin.orders.buyer}</div>
                      <div className="mt-0.5 font-grotesk text-ink">{o.name}</div>
                      <div className="font-mono text-xs text-mutedink">{o.email}</div>
                    </div>
                    <div>
                      <div className="font-mono text-[9px] uppercase tracking-widest text-mutedink">{t.admin.orders.address}</div>
                      <div className="mt-0.5 whitespace-pre-wrap font-grotesk text-ink">{o.address}</div>
                    </div>
                    <div>
                      <div className="font-mono text-[9px] uppercase tracking-widest text-mutedink">{t.admin.orders.ordered}</div>
                      <div className="mt-0.5 font-mono text-xs text-ink">{fmtDate(o.created_at)}</div>
                      <div className="mt-2 font-mono text-[9px] uppercase tracking-widest text-mutedink">{t.admin.orders.reservedUntil}</div>
                      <div className="mt-0.5 font-mono text-xs text-ink">{fmtDate(o.reserved_until)}</div>
                    </div>
                  </div>
                  {o.status === "pending" && (
                    <div className="mt-4 flex gap-2">
                      <button data-testid={`order-paid-${o.id}`} onClick={() => orderAction(o, "paid")} className="bg-okgreen px-4 py-2.5 font-grotesk text-xs font-bold uppercase tracking-widest text-white transition-colors hover:opacity-90">
                        {t.admin.orders.markPaid}
                      </button>
                      <button data-testid={`order-cancel-${o.id}`} onClick={() => orderAction(o, "cancelled")} className="border border-signal/50 px-4 py-2.5 font-grotesk text-xs uppercase tracking-widest text-signal transition-colors hover:bg-signal hover:text-white">
                        {t.admin.orders.cancel}
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {tab === "messages" && (
          <div className="mt-8 grid grid-cols-1 gap-4" data-testid="admin-messages-list">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center gap-3 border border-dashed border-edge-bright py-16 text-mutedink" data-testid="admin-messages-empty">
                <Inbox size={28} />
                <span className="font-grotesk text-sm">{t.admin.noMessages}</span>
              </div>
            ) : (
              messages.map((m) => (
                <div key={m.id} data-testid={`admin-message-${m.id}`} className="border border-edge bg-surface p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-grotesk font-semibold text-ink">{m.name} <span className="font-mono text-xs font-normal text-mutedink">&lt;{m.email}&gt;</span></span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-mutedink">{fmtDate(m.created_at)}</span>
                  </div>
                  {m.product_ref && <div className="mt-2 font-mono text-xs text-volt">{m.product_ref}</div>}
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-mutedink">{m.message}</p>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "stats" && (
          <div className="mt-8 grid grid-cols-1 gap-4" data-testid="admin-stats-list">
            {stats.length === 0 ? (
              <div className="border border-dashed border-edge-bright py-16 text-center text-mutedink" data-testid="admin-stats-empty">
                <span className="font-grotesk text-sm">{t.admin.stats.empty}</span>
              </div>
            ) : (
              stats.map((p, i) => (
                <div key={p.id} data-testid={`admin-stats-row-${p.code}`} className="flex flex-wrap items-center gap-4 border border-edge bg-surface p-4">
                  <span className="w-8 font-mono text-sm font-bold text-edge-bright">{String(i + 1).padStart(2, "0")}</span>
                  <img src={imgSrc(p.image_url)} alt={p.title} className="h-14 w-14 border border-edge object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-mutedink">[{p.code}] · {p.category}</div>
                    <div className="truncate font-grotesk font-semibold text-ink">{p.title}</div>
                  </div>
                  <div className="flex gap-px border border-edge font-mono text-center">
                    <div className="border-r border-edge px-4 py-2">
                      <div className="text-base font-bold text-volt" data-testid={`stats-views-${p.code}`}>{p.stats.views}</div>
                      <div className="text-[9px] uppercase tracking-widest text-mutedink">{t.admin.stats.views}</div>
                    </div>
                    <div className="border-r border-edge px-4 py-2">
                      <div className="text-base font-bold text-ink" data-testid={`stats-ig-${p.code}`}>{p.stats.ig_clicks}</div>
                      <div className="text-[9px] uppercase tracking-widest text-mutedink">{t.admin.stats.ig}</div>
                    </div>
                    <div className="border-r border-edge px-4 py-2">
                      <div className="text-base font-bold text-ink" data-testid={`stats-vinted-${p.code}`}>{p.stats.vinted_clicks || 0}</div>
                      <div className="text-[9px] uppercase tracking-widest text-mutedink">{t.admin.stats.vinted}</div>
                    </div>
                    <div className="px-4 py-2">
                      <div className="text-base font-bold text-ink" data-testid={`stats-email-${p.code}`}>{p.stats.email_clicks}</div>
                      <div className="text-[9px] uppercase tracking-widest text-mutedink">{t.admin.stats.email}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "drop" && (
          <>
            <form onSubmit={(e) => saveSettings(e, t.admin.dropSaved)} className="mt-8 max-w-2xl border border-edge-bright bg-surface p-6 shadow-[0_2px_16px_rgba(18,20,23,0.05)] md:p-8" data-testid="admin-drop-form">
              <h2 className="mb-2 font-display text-lg font-bold tracking-tight text-ink">{t.admin.dropSettings}</h2>
              <p className="mb-6 font-mono text-[10px] uppercase tracking-widest text-mutedink">Next Drop // Countdown</p>
              <label className="flex cursor-pointer items-center gap-3" data-testid="drop-enabled-toggle">
                <input type="checkbox" data-testid="drop-enabled-checkbox" checked={settings.drop_enabled} onChange={(e) => setSettings({ ...settings, drop_enabled: e.target.checked })} className="h-4 w-4 accent-[#1B2A4A]" />
                <span className="font-mono text-xs uppercase tracking-widest text-ink">{t.admin.dropEnabled}</span>
              </label>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label={t.admin.dropDate} testid="drop-date-field">
                  <input data-testid="drop-date-input" type="datetime-local" value={settings.next_drop_at} onChange={(e) => setSettings({ ...settings, next_drop_at: e.target.value })} className={inputCls} />
                </Field>
                <div className="hidden sm:block" />
                <Field label={t.admin.dropTitleDe} testid="drop-title-de-field">
                  <input data-testid="drop-title-de-input" value={settings.drop_title_de} onChange={(e) => setSettings({ ...settings, drop_title_de: e.target.value })} className={inputCls} placeholder="Nächster Drop" />
                </Field>
                <Field label={t.admin.dropTitleEn} testid="drop-title-en-field">
                  <input data-testid="drop-title-en-input" value={settings.drop_title_en} onChange={(e) => setSettings({ ...settings, drop_title_en: e.target.value })} className={inputCls} placeholder="Next Drop" />
                </Field>
              </div>
              <button data-testid="drop-save-button" type="submit" className="mt-7 bg-volt px-6 py-3 font-grotesk text-xs font-bold uppercase tracking-widest text-obsidian transition-colors hover:bg-volt-hover">
                {t.admin.save}
              </button>
            </form>

            <div className="mt-8 max-w-2xl" data-testid="admin-subscribers">
              <div className="mb-4 flex items-center gap-2">
                <Bell size={14} className="text-volt" />
                <h3 className="font-display text-base font-bold tracking-tight text-ink">{t.admin.subscribers} ({subscribers.length})</h3>
              </div>
              {subscribers.length === 0 ? (
                <div className="border border-dashed border-edge-bright py-10 text-center" data-testid="admin-subscribers-empty">
                  <span className="font-grotesk text-sm text-mutedink">{t.admin.noSubscribers}</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2" data-testid="admin-subscribers-list">
                  {subscribers.map((s) => (
                    <div key={s.id} data-testid={`admin-subscriber-${s.id}`} className="flex items-center justify-between border border-edge bg-surface px-4 py-3">
                      <span className="font-grotesk text-sm text-ink">{s.email}</span>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-mutedink">{fmtDate(s.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {tab === "bank" && (
          <form onSubmit={(e) => saveSettings(e, t.admin.bank.saved)} className="mt-8 max-w-2xl border border-edge-bright bg-surface p-6 shadow-[0_2px_16px_rgba(18,20,23,0.05)] md:p-8" data-testid="admin-bank-form">
            <div className="mb-2 flex items-center gap-2">
              <Landmark size={16} className="text-volt" />
              <h2 className="font-display text-lg font-bold tracking-tight text-ink">{t.admin.bank.title}</h2>
            </div>
            <p className="mb-6 font-mono text-[10px] uppercase tracking-widest text-mutedink">{t.admin.bank.sub}</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label={t.admin.bank.holder} testid="bank-holder-field">
                <input data-testid="bank-holder-input" required value={settings.bank_holder} onChange={(e) => setSettings({ ...settings, bank_holder: e.target.value })} className={inputCls} />
              </Field>
              <Field label={t.admin.bank.name} testid="bank-name-field">
                <input data-testid="bank-name-input" value={settings.bank_name} onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })} className={inputCls} />
              </Field>
              <Field label={t.admin.bank.iban} testid="bank-iban-field">
                <input data-testid="bank-iban-input" required value={settings.bank_iban} onChange={(e) => setSettings({ ...settings, bank_iban: e.target.value })} className={`${inputCls} font-mono`} />
              </Field>
              <Field label={t.admin.bank.bic} testid="bank-bic-field">
                <input data-testid="bank-bic-input" value={settings.bank_bic} onChange={(e) => setSettings({ ...settings, bank_bic: e.target.value })} className={`${inputCls} font-mono`} />
              </Field>
            </div>
            <button data-testid="bank-save-button" type="submit" className="mt-7 bg-volt px-6 py-3 font-grotesk text-xs font-bold uppercase tracking-widest text-obsidian transition-colors hover:bg-volt-hover">
              {t.admin.save}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
